import logoSrc from "@/assets/matchbroker-logo.png";
import logoHeaderSrc from "@/assets/matchbroker-logo-header.png";
import { cn } from "@/lib/utils";

/**
 * MatchBroker official brand marks.
 *
 * The source of truth is the user-provided PNG (green M + navy B monogram,
 * transparent background). Two exported components:
 *
 *   BrandLogo      — horizontal lockup: monogram + split-color "MatchBroker"
 *                    text (Match=green #35622F, Broker=navy #1B2B4B).
 *                    Use on auth pages, landing, verification-pending,
 *                    mock checkout, billing success/cancelled.
 *   BrandMonogram  — the monogram alone. Use in the compact app header
 *                    (a real "MatchBroker" text label sits next to it in
 *                    AppHeader.jsx).
 */
export function BrandLogo({ className, alt = "MatchBroker", wordmarkClassName }) {
  // Callers pass a height class (e.g. h-10). Route it to the <img> so the
  // monogram sizes correctly; the text mark auto-scales via container gap.
  // `wordmarkClassName` lets callers hide the wordmark on narrow viewports
  // (e.g. LandingPage header uses `hidden sm:inline` to prevent overlap
  // with the CTA buttons at 320–430px).
  return (
    <span
      className="inline-flex items-center gap-2 select-none"
      data-testid="brand-logo"
    >
      <img
        src={logoSrc}
        alt=""
        aria-hidden
        draggable={false}
        className={cn("w-auto object-contain", className || "h-8")}
      />
      <span
        className={cn(
          "text-xl font-bold tracking-tight leading-none whitespace-nowrap",
          wordmarkClassName,
        )}
      >
        <span className="text-brand-green">Match</span>
        <span className="text-brand-navy">Broker</span>
      </span>
      <span className="sr-only">{alt}</span>
    </span>
  );
}

export function BrandMonogram({ className, alt = "MatchBroker", ...rest }) {
  return (
    <img
      src={logoHeaderSrc}
      alt={alt}
      draggable={false}
      className={cn("h-8 w-auto object-contain select-none", className)}
      data-testid="brand-monogram"
      {...rest}
    />
  );
}
